import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { ConfirmModal } from '../ds/ConfirmModal';
import { useAuthOptional } from '../contexts/AuthContext';
import { ceebUnlinkCaseApi } from '../services/api';
import type { CeebUnlinkCaseCheck } from '../services/api';
import { ceebDeletedFull } from '../utils/ceebDeletedProtocol';

interface Props {
  buildingId: string;
  /** Po udanym odpięciu rodzic przeładowuje budynek (numer sprawy, etap). */
  onUnlinked?: () => void;
}

const BANER: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  padding: 'var(--space-3) var(--space-4)',
  borderRadius: 'var(--radius-sm)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-body)',
};

/**
 * RAPORT G8 (2026-10-19): „Odepnij numer sprawy usuniętej w CEEB" na karcie budynku — TYLKO superadmin.
 *
 * Gdy protokół usunięto w CEEB (kosz), przegląd zachowywał numer sprawy, a każda wysyłka kończyła się odmową bota
 * „Nie znaleziono protokołu o numerze sprawy …" (TARGET_NOT_FOUND). Odpięcie robiło się SQL-em (Pokoju 21, 9.10).
 * O dostępności decyduje WYŁĄCZNIE serwer (`ceeb-unlink-case-check`: ostatnia operacja bota = ta odmowa dla
 * zapisanego numeru, nic nie trwa, protokół niezaakceptowany) — panel pokazuje akcję tylko przy `available`, a numer
 * do POST bierze z odpowiedzi serwera. Poza tym stanem panel milczy.
 * 074 (2026-10-21): drugi dowód — nocny pełny skan nie widzi protokołu w CEEB (`evidence: 'missing-in-scan'`, Prusa 7);
 * wtedy baner mówi o usunięciu wykrytym przez skan, nie o odmowie bota.
 */
const CeebUnlinkCasePanel: React.FC<Props> = ({ buildingId, onUnlinked }) => {
  const auth = useAuthOptional();
  const isSuperadmin = auth?.user?.role === 'superadmin';
  const [check, setCheck] = useState<CeebUnlinkCaseCheck | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    setCheck(null);
    setDone(null);
    setError(null);
    if (!isSuperadmin) return undefined;
    let aktualne = true;
    ceebUnlinkCaseApi.check(buildingId)
      .then((c) => { if (aktualne) setCheck(c); })
      // Sprawdzenie jest pomocnicze — przy błędzie akcji po prostu nie pokazujemy (wysyłka i tak pokaże odmowę bota).
      .catch((e) => { console.warn('[CeebUnlinkCasePanel] sprawdzenie odpięcia niedostępne:', e); if (aktualne) setCheck(null); });
    return () => { aktualne = false; };
  }, [buildingId, isSuperadmin]);

  const caseNumber = check?.caseNumber || '';

  const handleConfirm = useCallback(async () => {
    setAsking(false);
    if (!caseNumber) return;
    setBusy(true);
    setError(null);
    try {
      await ceebUnlinkCaseApi.unlink(buildingId, caseNumber);
      setDone(`Odpięto numer sprawy ${caseNumber}. Następne „Wyślij do CEEB” uruchomi Fazę A.`);
      setCheck(null);
      onUnlinked?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się odpiąć numeru sprawy.');
    } finally {
      setBusy(false);
    }
  }, [buildingId, caseNumber, onUnlinked]);

  if (!isSuperadmin) return null;
  if (done) {
    return (
      <div
        role="status"
        data-testid="ceeb-unlink-case-done"
        style={{ ...BANER, marginBottom: 'var(--space-5)', background: 'var(--status-ok-bg)', borderLeft: '3px solid var(--status-ok-bar)', color: 'var(--status-ok)' }}
      >
        <Icon name="check-circle" size={18} />
        <span>{done}</span>
      </div>
    );
  }
  if (!check || !check.available || !caseNumber) return null;

  const rok = new Date().getFullYear();
  return (
    <div
      data-testid="ceeb-unlink-case-panel"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}
    >
      <div
        role="status"
        style={{ ...BANER, background: 'var(--status-rec-bg)', borderLeft: '3px solid var(--status-rec-bar)', color: 'var(--status-rec)' }}
      >
        <Icon name="triangle-alert" size={18} />
        <span>
          {check.evidence === 'missing-in-scan' ? (
            <>
              {ceebDeletedFull(caseNumber, check.deletedInCeebAt)} Przegląd nadal ma ten numer sprawy — odepnij go, inaczej
              każda wysyłka będzie celować w usunięty protokół.
            </>
          ) : (
            <>
              Ostatnia wysyłka do CEEB zakończyła się odmową bota: „Nie znaleziono protokołu o numerze sprawy {caseNumber}”.
              Jeśli protokół usunięto w CEEB (kosz), odepnij jego numer od przeglądu — inaczej każda kolejna wysyłka skończy się tak samo.
            </>
          )}
        </span>
      </div>
      {error && (
        <div
          role="alert"
          style={{ ...BANER, background: 'var(--status-deny-bg)', borderLeft: '3px solid var(--status-deny-bar)', color: 'var(--status-deny)' }}
        >
          <Icon name="triangle-alert" size={18} />
          <span>{error}</span>
        </div>
      )}
      <div>
        <Button icon="triangle-alert" disabled={busy} onClick={() => setAsking(true)}>
          {busy ? 'Odpinanie…' : 'Odepnij numer sprawy usuniętej w CEEB'}
        </Button>
      </div>
      <ConfirmModal
        open={asking}
        title="Odepnij numer sprawy usuniętej w CEEB"
        destructive
        confirmLabel={`Odepnij ${caseNumber}`}
        message={(
          <>
            <p style={{ margin: '0 0 var(--space-3)' }}>
              Odpiąć numer sprawy <strong>{caseNumber}</strong> od bieżącego przeglądu? Przegląd straci numer sprawy i status CEEB;
              dane z terenu zostają bez zmian. Zapisy lokali zaimportowane z tego protokołu przestaną liczyć się jako „w CEEB”
              (wyśle je następna wysyłka). W CEEB nic się nie zmienia.
            </p>
            <p style={{ margin: '0 0 var(--space-3)' }}>
              Następne „Wyślij do CEEB” uruchomi Fazę A: znajdzie albo założy protokół {rok} na istniejącej nieruchomości
              w CEEB — nigdy drugiej rejestracji budynku.
            </p>
            <p style={{ margin: 0 }}>Odpinaj tylko, jeśli protokół {caseNumber} naprawdę usunięto w CEEB.</p>
          </>
        )}
        onConfirm={() => { void handleConfirm(); }}
        onCancel={() => setAsking(false)}
      />
    </div>
  );
};

export default CeebUnlinkCasePanel;
